import { FC, useEffect, useState } from 'react';
import { IonRangeCustomEvent } from '@ionic/core';
import { IonRange, RangeChangeEventDetail } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { indexOfClosest, isSorted } from '../utils/arrays';
import { Text } from './Text';

const useStyles = createUseStyles({
    rangeWrapper: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 10,

        '& > ion-text': {
            minWidth: 50,
            textAlign: 'right'
        },

        '& ion-range::part(bar-active)': {
            borderRadius: 14
        },

        '& ion-range::part(tick-active)': {
            display: 'none !important'
        },

        '& ion-range::part(tick)': {
            display: 'none !important'
        }
    },

    range: {
        flexGrow: 1,
        '--knob-size': '20px',
        '--bar-height': '14px',
        '--bar-border-radius': '14px',
        '--bar-background': 'var(--ion-color-light)'
    }
});

export type ISliderProps = {
    'data-type'?: string;
    'data-id'?: string;
    min: number;
    max: number;
    steps?: number[];
    value: number;
    activeColor?: string;
    onChange: (val: number) => void;
    showLabel?: boolean;
    labelFormatter?: (value: number | undefined) => string;
};

export const Slider: FC<ISliderProps> = ({ 'data-type': dataType, 'data-id': dataId, min, max, steps, value, activeColor, onChange, showLabel, labelFormatter }) => {
    const classes = useStyles();

    const toInternal = (external: number) => {
        return steps ? steps[external - min] : external;
    };

    const toExternal = (internal: number) => {
        return steps ? indexOfClosest(steps, internal) + min : internal;
    };

    const [internalValue, setInternalValue] = useState(toInternal(value));

    useEffect(() => {
        if (value < min || value > max) {
            throw new Error(`value (${value}) must be between min (${min}) and max (${max})`);
        }
        setInternalValue(toInternal(value));
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    // hard to check steos at compile time, so validate at run time
    useEffect(() => {
        if (steps && steps.length !== (max - min + 1)) {
            throw new Error(`steps array size must be (max - min + 1) = ${max - min + 1}`);
        }
        if (steps && steps.some(n => n !== Math.floor(n))) {
            throw new Error('steps array must contain only integers');
        }
        if (steps && !isSorted(steps)) {
            throw new Error('steps array must be sorted');
        }
    }, [steps, min, max]);

    const handleInput = (ev: IonRangeCustomEvent<RangeChangeEventDetail>) => {
        setInternalValue(ev.detail.value as number);
    };

    const handleChange = (ev: IonRangeCustomEvent<RangeChangeEventDetail>) => {
        try {
            const newValue = toExternal(ev.detail.value as number);
            onChange(newValue);

            // replicate "snap" behavior
            setInternalValue(toInternal(newValue));

        } catch (e) {
            // revert change (timeout needed when slider is moved by clicking)
            setTimeout(() => setInternalValue(toInternal(value)));
        }
    };

    return (
        <div className={classes.rangeWrapper}>
            <IonRange
                data-type={dataType}
                data-id={dataId}
                snaps
                min={steps ? steps[0] : min}
                max={steps ? steps[steps.length - 1] : max}
                mode="ios"
                value={internalValue}
                className={classes.range}
                onIonInput={handleInput}
                onIonChange={handleChange}
                style={{
                    '--knob-background': `var(--ion-color-${activeColor})`,
                    '--bar-background-active': `rgb(var(--ion-color-${activeColor}-rgb), 0.3)`
                }}
            />
            {showLabel &&
                <Text>{labelFormatter ? labelFormatter(toExternal(internalValue)) : toExternal(internalValue)}</Text>
            }
        </div>
    );
};
